<template>
  <div class="slide-shared-link-menu" :class="{ open: isOpen, plot: isPlot, hide: !isOpen && kind === 'manuscript' }">
    <div class="tag" @click="onOpenTagClick">
      <span v-if="isOpen"><close-icon /></span>
      <span v-else><comment-icon :isUnread="hasUnreadComment" /></span>
    </div>
    <comment-list-on-slide-menu
      v-if="selectedSharedLinkId && selectedContentKey"
      class="list"
      :novelId="novelId"
      :sharedLinkId="selectedSharedLinkId"
      :contentKey="selectedContentKey"
      :kind="kind"
    />
    <shared-link-content-list-on-slide-menu
      v-else-if="selectedSharedLinkId"
      class="list"
      :novelId="novelId"
      :kind="kind"
    />
    <shared-link-list-on-slide-menu v-else class="list" :novelId="novelId" :kind="kind" />
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import CommentListOnSlideMenu from "@/components/organisms/CommentListOnSlideMenu.vue";
import SharedLinkContentListOnSlideMenu from "@/components/organisms/SharedLinkContentListOnSlideMenu.vue";
import SharedLinkListOnSlideMenu from "@/components/organisms/SharedLinkListOnSlideMenu.vue";
import CommentIcon from "@/components/atoms/CommentIcon.vue";
import CloseIcon from "@/components/atoms/CloseIcon.vue";
import { Comment as CommentDexie } from "@/lib/indexeddb";
import { User, SharedLinkKind } from "@/lib/models";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    CommentListOnSlideMenu,
    SharedLinkContentListOnSlideMenu,
    SharedLinkListOnSlideMenu,
    CommentIcon,
    CloseIcon,
  },
  props: {
    novelId: {
      type: String as PropType<string>,
    },
    kind: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  methods: {
    onOpenTagClick() {
      this.isOpen = !this.isOpen;
    },
    closeMenu() {
      this.isOpen = false;
    },
  },
  computed: {
    user() {
      return this.$store.getters["userModule/user"] as User;
    },
    selectedSharedLinkId() {
      return this.$store.getters["sharedLinkModule/selectedSharedLinkId"];
    },
    selectedContentKey() {
      return this.$store.getters["sharedLinkModule/selectedContentKey"];
    },
    hasUnreadComment() {
      const { novelId, kind } = this;
      const { userId } = this.user as User;
      const unreadCommentList = this.$store.getters["sharedLinkModule/unreadCommentList"](
        userId,
        novelId,
        kind
      ) as CommentDexie[];
      return !!unreadCommentList.length;
    },
    isPlot() {
      return this.kind === SharedLinkKind.PLOT;
    },
  },
  destroyed() {
    this.$store.dispatch("sharedLinkModule/initializeSelectedSharedLink");
    this.$store.dispatch("sharedLinkModule/initializeSelectedContent");
  },
  watch: {
    $route: "closeMenu",
  },
});

interface Props {
  novelId: string;
  kind: string;
}

interface Data {
  isOpen: Boolean;
}

interface Methods {
  onOpenTagClick: () => void;
  closeMenu: () => void;
}

interface Computed {
  user: User;
  selectedSharedLinkId: string;
  selectedContentKey: string;
  hasUnreadComment: boolean;
  isPlot: boolean;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
.slide-shared-link-menu {
  display: flex;
  padding: 0px;
  transition: 0.5s;
  overflow: hidden;
  height: 100%;
  width: 37px;
  pointer-events: none;

  &.hide {
    width: 0px;
  }

  &.open {
    width: 350px;
  }

  &.plot {
    margin-top: 60px;
  }

  &.open.plot {
    margin-top: 0px;
    height: 100%;
  }

  .tag {
    margin-top: 32px;
    margin-bottom: auto;
    padding: 8px;
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
    border: 1px solid var(--color-border);
    border-right: none;
    user-select: none;
    writing-mode: vertical-lr;
    background-color: var(--color-surface-subtle);
    cursor: pointer;
    pointer-events: auto;
    color: var(--color-text-normal);
  }

  .list {
    pointer-events: auto;
  }
}
</style>
